import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Plus, Save, RefreshCcw, Search } from "lucide-react";

export function UsersAdmin() {
    const emptyForm = {
        email: "",
        password: "",
        name: "",
        role: "sales_agent",
        branch: "",
        department: "",
        business_mode: "organic",
        price_visibility: "dealers",
        company: "",
        assigned_company_customer_code: "",
        assigned_company_customer_name: "",
        can_create_replenishment: false,
        replenishment_source_schema: "",
        replenishment_destination_schema: "",
        replenishment_destination_warehouse: "",
    };

    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [schemas, setSchemas] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [resetPassword, setResetPassword] = useState("");
    const [form, setForm] = useState(emptyForm);
    const [q, setQ] = useState("");
    const filteredUsers = users.filter((u) => {
        if (!q.trim()) return true;
        const s = q.toLowerCase();
        return [u.name, u.email, u.role, u.company, u.branch, u.department, u.assigned_company_customer_name]
            .some((v) => String(v || "").toLowerCase().includes(s));
    });

    const load = async () => {
        try {
            const [u, r, c, sch, wh] = await Promise.all([
                api.get("/users"),
                api.get("/roles"),
                api.get("/customers").catch(() => ({ data: [] })),
                api.get("/admin/schemas").catch(() => ({ data: [] })),
                api.get("/admin/warehouses").catch(() => ({ data: [] })),
            ]);

            setUsers(u.data);
            setRoles(r.data);
            setCustomers(c.data || []);
            setSchemas(sch.data || []);
            setWarehouses(wh.data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { load(); }, []);

    const customerOptions = customers
        .map((c) => ({
            code: c.customer_code,
            name: c.name || c.customer_name || c.customer_code,
            label: `${c.name || c.customer_name || c.customer_code}${c.customer_code ? ` · ${c.customer_code}` : ""}`,
        }))
        .filter((x) => x.code);

    const schemaOptions = schemas
        .map((x) => ({
            schema_name: x.schema_name,
            display_name: x.display_name || x.schema_name,
            schema_type: x.schema_type || "",
        }))
        .filter((x) => x.schema_name);

    const nonOrganicSchemas = schemaOptions.filter((x) =>
        x.schema_type === "Non-Organic" ||
        ["DWELLSTONE", "MOZZAICO", "Z_SBOTEST_DWELLSTONE"].includes(String(x.schema_name).toUpperCase())
    );

    const organicSchemas = schemaOptions.filter((x) =>
        x.schema_type === "Organic" &&
        !["DWELLSTONE", "MOZZAICO", "Z_SBOTEST_DWELLSTONE"].includes(String(x.schema_name).toUpperCase())
    );

    const destinationWarehouseOptions = warehouses.filter((w) =>
        !editing?.replenishment_destination_schema ||
        w.schema_name === editing.replenishment_destination_schema
    );

    const create = async (e) => {
        e.preventDefault();

        try {
            await api.post("/users", form);
            toast.success("User created");
            setCreating(false);
            setForm(emptyForm);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const saveUser = async () => {
        if (!editing) return;

        try {
            await api.put(`/users/${editing.id}`, {
                email: editing.email,
                name: editing.name,
                role: editing.role,
                branch: editing.branch,
                department: editing.department,
                status: editing.status || "active",
                business_mode: editing.business_mode || "organic",
                price_visibility: editing.price_visibility || "dealers",
                company: editing.company || "",
                assigned_company_customer_code: editing.assigned_company_customer_code || "",
                assigned_company_customer_name: editing.assigned_company_customer_name || "",
                can_create_replenishment: !!editing.can_create_replenishment,
                replenishment_source_schema: editing.replenishment_source_schema || "",
                replenishment_destination_schema: editing.replenishment_destination_schema || "",
                replenishment_destination_warehouse: editing.replenishment_destination_warehouse || "",
            });

            toast.success("User updated");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const resetUserPassword = async () => {
        if (!resetting) return;

        if (!resetPassword || resetPassword.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }

        try {
            await api.put(`/users/${resetting.id}`, { password: resetPassword });
            toast.success("Password reset");
            setResetting(null);
            setResetPassword("");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const quickUpdate = async (userId, payload) => {
        try {
            await api.put(`/users/${userId}`, payload);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const selectCompanyCustomer = (target, setter, code) => {
        const selected = customerOptions.find((c) => c.code === code) || {};
        setter({
            ...target,
            assigned_company_customer_code: selected.code || "",
            assigned_company_customer_name: selected.name || "",
            company: target.company || selected.name || "",
        });
    };

    return (
        <div className="p-6 space-y-4" data-testid="users-admin-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="sc-section-title">Administration</div>
                    <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage users, User Type (Organic/Non-Organic), company assignment, branch, role, status, and password resets.
                    </p>
                </div>

                <button
                    onClick={() => setCreating((s) => !s)}
                    className="h-9 inline-flex items-center gap-1 px-3 rounded-md border border-border text-sm hover:bg-secondary"
                    data-testid="user-create-toggle"
                >
                    <Plus className="w-4 h-4" /> {creating ? "Cancel" : "New user"}
                </button>
            </div>

            {creating && (
                <form onSubmit={create} className="sc-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="user-create-form">
                    <input className="sc-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                    <input className="sc-input" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                    <input className="sc-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />

                    <select className="sc-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                        {roles.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                    </select>

                    <select className="sc-input" title="User Type" value={form.business_mode} onChange={(e) => setForm({ ...form, business_mode: e.target.value })}>
                        <option value="organic">User Type: Organic</option>
                        <option value="non_organic">User Type: Non-Organic</option>
                    </select>

                    <input className="sc-input" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />

                    <input className="sc-input" placeholder="Branch / Store" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} />
                    <input className="sc-input" placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />

                    {form.business_mode === "non_organic" && (
                        <select className="sc-input" value={form.assigned_company_customer_code || ""} onChange={(e) => selectCompanyCustomer(form, setForm, e.target.value)}>
                            <option value="">Assigned Company Customer</option>
                            {customerOptions.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                        </select>
                    )}
                    {form.business_mode === "non_organic" && (
                        <select className="sc-input" title="Price visibility" value={form.price_visibility || "dealers"} onChange={(e) => setForm({ ...form, price_visibility: e.target.value })}>
                            <option value="none">Prices: Hidden (Non-organic 1)</option>
                            <option value="distributor">Prices: Distributor + Promo (2A)</option>
                            <option value="dealers">Prices: Dealers + Promo (2B)</option>
                            <option value="srp2">Prices: SRP2, no Promo (2C)</option>
                        </select>
                    )}

                    <div className="md:col-span-3 flex justify-end">
                        <button type="submit" className="h-9 px-3 inline-flex items-center gap-1 rounded-md bg-foreground text-background text-sm">
                            <Save className="w-4 h-4" /> Create
                        </button>
                    </div>
                </form>
            )}

            <div className="sc-card p-3">
                <div className="relative max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input className="sc-input pl-9" placeholder="Search users — name, email, role, company…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
                </div>
                {q.trim() && <div className="text-xs text-muted-foreground mt-1.5">{filteredUsers.length} of {users.length} users</div>}
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1250px]">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Name</th>
                                <th>Role</th>
                                <th>User Type</th>
                                <th>Company</th>
                                <th>Branch</th>
                                <th>Assigned Customer</th>
                                <th>Replenishment</th>
                                <th>Status</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredUsers.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-muted-foreground">No matching users.</td></tr>}
                            {filteredUsers.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                    <td className="font-mono text-xs">{u.email}</td>
                                    <td>{u.name}</td>
                                    <td>
                                        <select value={u.role} onChange={(e) => quickUpdate(u.id, { role: e.target.value })} className="sc-input h-7 text-xs w-44">
                                            {roles.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                                        </select>
                                    </td>
                                    <td>{u.business_mode || "organic"}</td>
                                    <td>{u.company || "—"}</td>
                                    <td>{u.branch || "—"}</td>
                                    <td className="text-xs">
                                        {u.assigned_company_customer_name || u.assigned_company_customer_code || "—"}
                                    </td>
                                    <td className="text-xs">
                                        {u.can_create_replenishment ? (
                                            <span>
                                                Yes
                                                <span className="block text-muted-foreground">
                                                    {u.replenishment_source_schema || "—"} → {u.replenishment_destination_schema || "—"}
                                                </span>
                                            </span>
                                        ) : "—"}
                                    </td>
                                    <td>
                                        <select value={u.status || "active"} onChange={(e) => quickUpdate(u.id, { status: e.target.value })} className="sc-input h-7 text-xs w-28">
                                            <option value="active">active</option>
                                            <option value="inactive">inactive</option>
                                        </select>
                                    </td>
                                    <td className="text-right space-x-1">
                                        <button type="button" onClick={() => setEditing({ business_mode: "organic", ...u })} className="h-7 px-2 rounded border border-border text-xs hover:bg-secondary">
                                            Edit
                                        </button>
                                        <button type="button" onClick={() => { setResetting(u); setResetPassword(""); }} className="h-7 px-2 rounded border border-border text-xs hover:bg-secondary inline-flex items-center gap-1">
                                            <RefreshCcw className="w-3 h-3" /> Reset Password
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setEditing(null)}>
                    <div className="sc-card p-5 w-full max-w-3xl space-y-3 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div className="font-semibold">Edit User</div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Email"><input className="sc-input mt-1 font-mono" type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
                            <Field label="Full Name"><input className="sc-input mt-1" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>

                            <Field label="Role">
                                <select className="sc-input mt-1" value={editing.role || ""} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                                    {roles.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                                </select>
                            </Field>

                            <Field label="User Type">
                                <select className="sc-input mt-1" value={editing.business_mode || "organic"} onChange={(e) => setEditing({ ...editing, business_mode: e.target.value })}>
                                    <option value="organic">Organic</option>
                                    <option value="non_organic">Non-Organic</option>
                                </select>
                            </Field>

                            <Field label="Company">
                                <input className="sc-input mt-1" value={editing.company || ""} onChange={(e) => setEditing({ ...editing, company: e.target.value })} />
                            </Field>

                            <Field label="Branch / Store">
                                <input className="sc-input mt-1" value={editing.branch || ""} onChange={(e) => setEditing({ ...editing, branch: e.target.value })} />
                            </Field>

                            <div className="md:col-span-2 border border-border rounded-md p-3 space-y-3">
                                <label className="flex items-center gap-2 text-sm font-medium">
                                    <input
                                        type="checkbox"
                                        checked={!!editing.can_create_replenishment}
                                        onChange={(e) => setEditing({ ...editing, can_create_replenishment: e.target.checked })}
                                    />
                                    Can create inventory replenishment from Non-Organic schema
                                </label>

                                {editing.can_create_replenishment && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <Field label="Source Non-Organic Schema">
                                            <select
                                                className="sc-input mt-1"
                                                value={editing.replenishment_source_schema || ""}
                                                onChange={(e) => setEditing({ ...editing, replenishment_source_schema: e.target.value })}
                                            >
                                                <option value="">Select source schema</option>
                                                {nonOrganicSchemas.map((x) => (
                                                    <option key={x.schema_name} value={x.schema_name}>
                                                        {x.display_name} · {x.schema_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>

                                        <Field label="Destination Organic Schema">
                                            <select
                                                className="sc-input mt-1"
                                                value={editing.replenishment_destination_schema || ""}
                                                onChange={(e) => setEditing({
                                                    ...editing,
                                                    replenishment_destination_schema: e.target.value,
                                                    replenishment_destination_warehouse: "",
                                                })}
                                            >
                                                <option value="">Select destination schema</option>
                                                {organicSchemas.map((x) => (
                                                    <option key={x.schema_name} value={x.schema_name}>
                                                        {x.display_name} · {x.schema_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>

                                        <Field label="Destination Warehouse">
                                            <select
                                                className="sc-input mt-1"
                                                value={editing.replenishment_destination_warehouse || ""}
                                                onChange={(e) => setEditing({ ...editing, replenishment_destination_warehouse: e.target.value })}
                                            >
                                                <option value="">Select warehouse</option>
                                                {destinationWarehouseOptions.map((w) => (
                                                    <option key={`${w.schema_name}-${w.warehouse_code}`} value={w.warehouse_code}>
                                                        {w.display_name || w.name || w.warehouse_code} · {w.warehouse_code}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>
                                    </div>
                                )}
                            </div>

                            {editing.business_mode === "non_organic" && (
                                <div className="md:col-span-2">
                                    <Field label="Assigned Company Customer">
                                        <select className="sc-input mt-1" value={editing.assigned_company_customer_code || ""} onChange={(e) => selectCompanyCustomer(editing, setEditing, e.target.value)}>
                                            <option value="">Select Customer</option>
                                            {editing.assigned_company_customer_code && !customerOptions.some((c) => c.code === editing.assigned_company_customer_code) && (
                                                <option value={editing.assigned_company_customer_code}>
                                                    {editing.assigned_company_customer_name || editing.assigned_company_customer_code}
                                                </option>
                                            )}
                                            {customerOptions.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                                        </select>
                                    </Field>
                                </div>
                            )}

                            <Field label="Department">
                                <input className="sc-input mt-1" value={editing.department || ""} onChange={(e) => setEditing({ ...editing, department: e.target.value })} />
                            </Field>

                            <Field label="Status">
                                <select className="sc-input mt-1" value={editing.status || "active"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                                    <option value="active">active</option>
                                    <option value="inactive">inactive</option>
                                </select>
                            </Field>
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-border">
                            <button onClick={() => setEditing(null)} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={saveUser} className="h-9 px-3 inline-flex items-center gap-1 rounded bg-foreground text-background text-sm">
                                <Save className="w-4 h-4" /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {resetting && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setResetting(null)}>
                    <div className="sc-card p-5 w-full max-w-md space-y-3 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div className="font-semibold">Reset Password</div>
                        <div className="text-xs text-muted-foreground">User: <span className="font-mono">{resetting.email}</span></div>
                        <input className="sc-input" type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Minimum 8 characters" />
                        <div className="flex justify-end gap-2 pt-2 border-t border-border">
                            <button onClick={() => setResetting(null)} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={resetUserPassword} className="h-9 px-3 inline-flex items-center gap-1 rounded bg-foreground text-background text-sm">
                                <Save className="w-4 h-4" /> Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs text-muted-foreground">{label}</label>
            {children}
        </div>
    );
}

export function RolesAdmin() {
    const [roles, setRoles] = useState([]);
    const [perms, setPerms] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setRoles((await api.get("/roles")).data);
        setPerms((await api.get("/permissions")).data);
    };
    useEffect(() => { load(); }, []);

    const togglePerm = async (role, perm) => {
        const has = role.permissions.includes(perm.code);
        const next = has ? role.permissions.filter((p) => p !== perm.code) : [...role.permissions, perm.code];
        setBusy(true);
        try { await api.put(`/roles/${role.code}`, { permissions: next }); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const grouped = perms.reduce((acc, p) => { (acc[p.module] ||= []).push(p); return acc; }, {});

    return (
        <div className="p-6 space-y-4" data-testid="roles-admin-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">Roles & Permission matrix</h1></div>

            {roles.map((role) => (
                <div key={role.code} className="sc-card" data-testid={`role-card-${role.code}`}>
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                        <div>
                            <div className="font-semibold">{role.name} <span className="text-muted-foreground text-xs font-mono">· {role.code}</span></div>
                            <div className="text-xs text-muted-foreground">{role.description}</div>
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">{role.permissions.length} permissions</div>
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(grouped).map(([mod, list]) => (
                            <div key={mod}>
                                <div className="sc-section-title mb-2">{mod}</div>
                                <div className="space-y-1">
                                    {list.map((p) => {
                                        const checked = role.permissions.includes(p.code);
                                        return (
                                            <label key={p.code} className="flex items-center gap-2 text-xs cursor-pointer">
                                                <input type="checkbox" checked={checked} onChange={() => togglePerm(role, p)} disabled={busy} data-testid={`perm-${role.code}-${p.code}`} />
                                                <span className="font-mono">{p.code}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export function EmailSetup() {
    const [s, setS] = useState(null);
    useEffect(() => { (async () => setS((await api.get("/email-settings")).data))(); }, []);
    if (!s) return <div className="p-6">Loading…</div>;
    const save = async () => {
        try { await api.put("/email-settings", s); toast.success("Saved (MOCK mode)"); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const f = (k, v) => setS({ ...s, [k]: v });
    return (
        <div className="p-6 space-y-4" data-testid="email-setup-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">Email Setup</h1></div>
            <div className="sc-card p-5 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
                <div><label className="text-xs text-muted-foreground">SMTP Host</label><input className="sc-input mt-1" value={s.smtp_host || ""} onChange={(e) => f("smtp_host", e.target.value)} data-testid="email-smtp-host" /></div>
                <div><label className="text-xs text-muted-foreground">SMTP Port</label><input className="sc-input mt-1" type="number" value={s.smtp_port || 587} onChange={(e) => f("smtp_port", Number(e.target.value))} data-testid="email-smtp-port" /></div>
                <div><label className="text-xs text-muted-foreground">SMTP User</label><input className="sc-input mt-1" value={s.smtp_user || ""} onChange={(e) => f("smtp_user", e.target.value)} /></div>
                <div><label className="text-xs text-muted-foreground">SMTP Password</label><input className="sc-input mt-1" type="password" value={s.smtp_password || ""} onChange={(e) => f("smtp_password", e.target.value)} /></div>
                <div><label className="text-xs text-muted-foreground">From email</label><input className="sc-input mt-1" value={s.from_email || ""} onChange={(e) => f("from_email", e.target.value)} /></div>
                <div><label className="text-xs text-muted-foreground">From name</label><input className="sc-input mt-1" value={s.from_name || ""} onChange={(e) => f("from_name", e.target.value)} /></div>
                <label className="flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={!!s.enabled} onChange={(e) => f("enabled", e.target.checked)} data-testid="email-enabled" /> Enabled (MOCK only — emails are logged to notifications)</label>
                <div className="md:col-span-2 flex justify-end"><button onClick={save} className="h-9 px-3 inline-flex items-center gap-1 rounded-md bg-foreground text-background text-sm" data-testid="email-save"><Save className="w-4 h-4" /> Save</button></div>
            </div>
        </div>
    );
}

export function EmailTemplates() {
    const [rows, setRows] = useState([]);
    const [edit, setEdit] = useState(null);
    const load = async () => setRows((await api.get("/email-templates")).data);
    useEffect(() => { load(); }, []);
    const save = async () => { await api.put(`/email-templates/${edit.code}`, edit); toast.success("Saved"); setEdit(null); await load(); };
    return (
        <div className="p-6 space-y-4" data-testid="email-templates-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">Email Templates</h1></div>
            <div className="sc-card">
                <table className="w-full sc-table">
                    <thead><tr><th>Code</th><th>Name</th><th>Subject</th><th></th></tr></thead>
                    <tbody>
                        {rows.map((t) => (
                            <tr key={t.code}>
                                <td className="font-mono">{t.code}</td>
                                <td>{t.name}</td>
                                <td className="text-muted-foreground">{t.subject}</td>
                                <td className="text-right"><button onClick={() => setEdit({ ...t })} className="text-xs h-7 px-2 rounded border border-border" data-testid={`template-edit-${t.code}`}>Edit</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {edit && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setEdit(null)}>
                    <div className="sc-card p-5 w-full max-w-2xl space-y-3 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div className="font-semibold">{edit.name}</div>
                        <input className="sc-input" value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} />
                        <textarea className="sc-input min-h-[200px] py-2" value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setEdit(null)} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={save} className="h-9 px-3 rounded bg-foreground text-background text-sm" data-testid="template-save">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function SAPConnection() {
    const [c, setC] = useState(null);
    useEffect(() => { (async () => setC((await api.get("/sap-connections")).data))(); }, []);
    if (!c) return <div className="p-6">Loading…</div>;
    const save = async () => { await api.put("/sap-connections", c); toast.success("Saved (MOCK)"); };
    const f = (k, v) => setC({ ...c, [k]: v });
    return (
        <div className="p-6 space-y-4" data-testid="sap-connection-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">SAP Connection</h1>
                <p className="text-sm text-muted-foreground mt-1">Mode: <StatusBadge status="MOCK" /> · Real HANA UAT connection will be wired post-MVP. Frontend never sees credentials directly.</p>
            </div>
            <div className="sc-card p-5 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
                <div><label className="text-xs text-muted-foreground">HANA Host</label><input className="sc-input mt-1" value={c.hana_host || ""} onChange={(e) => f("hana_host", e.target.value)} data-testid="sap-host" /></div>
                <div><label className="text-xs text-muted-foreground">HANA Port</label><input className="sc-input mt-1" type="number" value={c.hana_port || 30015} onChange={(e) => f("hana_port", Number(e.target.value))} /></div>
                <div><label className="text-xs text-muted-foreground">HANA User</label><input className="sc-input mt-1" value={c.hana_user || ""} onChange={(e) => f("hana_user", e.target.value)} /></div>
                <div><label className="text-xs text-muted-foreground">Schema</label><input className="sc-input mt-1" value={c.schema || "DWELLSTONE"} onChange={(e) => f("schema", e.target.value)} /></div>
                <div className="md:col-span-2"><label className="text-xs text-muted-foreground">Service Layer URL</label><input className="sc-input mt-1" value={c.service_layer_url || ""} onChange={(e) => f("service_layer_url", e.target.value)} placeholder="https://..." /></div>
                <div><label className="text-xs text-muted-foreground">Service Layer Company</label><input className="sc-input mt-1" value={c.service_layer_company || ""} onChange={(e) => f("service_layer_company", e.target.value)} /></div>
                <div><label className="text-xs text-muted-foreground">Service Layer User</label><input className="sc-input mt-1" value={c.service_layer_user || ""} onChange={(e) => f("service_layer_user", e.target.value)} /></div>
                <div className="md:col-span-2 flex justify-end"><button onClick={save} className="h-9 px-3 inline-flex items-center gap-1 rounded-md bg-foreground text-background text-sm" data-testid="sap-save"><Save className="w-4 h-4" /> Save</button></div>
            </div>
        </div>
    );
}

export function SyncMonitor() {
    const [logs, setLogs] = useState([]);
    const [health, setHealth] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [l, h] = await Promise.all([api.get("/sap-sync-logs"), api.get("/sap/health")]);
        setLogs(l.data); setHealth(h.data);
    };
    useEffect(() => { load(); }, []);

    const run = async (kind) => {
        setBusy(true);
        try { await api.post(`/sync/${kind}`); toast.success(`${kind} synced`); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="p-6 space-y-4" data-testid="sync-monitor-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">SAP Sync Monitor</h1></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="sc-card p-4">
                    <div className="sc-section-title mb-1">HANA Connectivity</div>
                    <StatusBadge status={health?.hana?.ok ? "active" : "failed"} />
                    <div className="text-xs text-muted-foreground mt-2 font-mono">{health?.hana?.host} · {health?.hana?.schema}</div>
                </div>
                <div className="sc-card p-4">
                    <div className="sc-section-title mb-1">Service Layer</div>
                    <StatusBadge status={health?.service_layer?.ok ? "active" : "failed"} />
                    <div className="text-xs text-muted-foreground mt-2 font-mono">{health?.service_layer?.service_layer}</div>
                </div>
                <div className="sc-card p-4 flex flex-col gap-2">
                    <div className="sc-section-title">Run sync</div>
                    <div className="flex flex-wrap gap-2">
                        {["products", "customers", "stock", "prices", "batches"].map((k) => (
                            <button key={k} onClick={() => run(k)} disabled={busy} className="h-8 px-3 inline-flex items-center gap-1 rounded border border-border text-xs hover:bg-secondary" data-testid={`sync-${k}`}>
                                <RefreshCcw className="w-3 h-3" /> {k}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="sc-card">
                <div className="px-4 py-3 border-b border-border"><h2 className="text-sm font-medium">Recent sync jobs</h2></div>
                <table className="w-full sc-table">
                    <thead><tr><th>When</th><th>Kind</th><th>Status</th><th className="text-right">Records</th><th>Mode</th></tr></thead>
                    <tbody>
                        {logs.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No sync history yet.</td></tr>}
                        {logs.map((l) => (
                            <tr key={l.id}>
                                <td className="text-muted-foreground">{new Date(l.started_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                <td className="font-mono">{l.kind}</td>
                                <td><StatusBadge status={l.status === "completed" ? "approved" : l.status} /></td>
                                <td className="sc-num">{l.synced_count}</td>
                                <td><StatusBadge status={l.mode || "MOCK"} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function PostingQueue() {
    const [rows, setRows] = useState([]);
    const load = async () => setRows((await api.get("/sap-posting-queue")).data);
    useEffect(() => { load(); }, []);
    const retry = async (job) => { await api.post(`/sap-posting-queue/${job.id}/retry`); toast.success("Retry queued"); await load(); };
    return (
        <div className="p-6 space-y-4" data-testid="posting-queue-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">SAP Posting Queue</h1></div>
            <div className="sc-card">
                <table className="w-full sc-table">
                    <thead><tr><th>When</th><th>Type</th><th>Doc no</th><th>Status</th><th className="text-right">Attempts</th><th></th></tr></thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Queue is empty.</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`queue-row-${r.id}`}>
                                <td className="text-muted-foreground">{new Date(r.created_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                <td>{r.document_type}</td>
                                <td className="font-mono">{r.document_no}</td>
                                <td><StatusBadge status={r.status} /></td>
                                <td className="sc-num">{r.attempts}</td>
                                <td className="text-right"><button onClick={() => retry(r)} className="text-xs h-7 px-2 rounded border border-border" data-testid={`queue-retry-${r.id}`}>Retry</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function ErrorLogs() {
    const [rows, setRows] = useState([]);
    useEffect(() => { (async () => setRows((await api.get("/sap-error-logs")).data))(); }, []);
    return (
        <div className="p-6 space-y-4" data-testid="error-logs-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">SAP Error Logs</h1></div>
            <div className="sc-card">
                <table className="w-full sc-table">
                    <thead><tr><th>When</th><th>Type</th><th>Doc</th><th>Message</th></tr></thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No errors logged.</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id}><td className="text-muted-foreground">{new Date(r.created_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td><td>{r.document_type}</td><td className="font-mono">{r.document_no}</td><td className="text-red-600">{r.message}</td></tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function AuditLogs() {
    const [rows, setRows] = useState([]);
    useEffect(() => { (async () => setRows((await api.get("/audit-logs")).data))(); }, []);
    return (
        <div className="p-6 space-y-4" data-testid="audit-logs-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">Audit Logs</h1></div>
            <div className="sc-card">
                <table className="w-full sc-table">
                    <thead><tr><th>When</th><th>User</th><th>Role</th><th>Action</th><th>Entity</th><th>ID</th></tr></thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No audit entries.</td></tr>}
                        {rows.slice(0, 200).map((r) => (
                            <tr key={r.id}>
                                <td className="text-muted-foreground">{new Date(r.created_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                <td className="font-mono text-xs">{r.user_email || "—"}</td>
                                <td>{r.user_role || "—"}</td>
                                <td className="font-mono">{r.action}</td>
                                <td>{r.entity}</td>
                                <td className="font-mono text-xs text-muted-foreground">{r.entity_id || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


export function ApproverAssignments() {
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [payload, setPayload] = useState(null);
    const [aq, setAq] = useState("");

    const loadUsers = async () => {
        const { data } = await api.get("/users");
        setUsers(data);
        if (!selectedUserId && data[0]) setSelectedUserId(data[0].id);
    };

    const loadAssignment = async (uid) => {
        if (!uid) return;
        try {
            const { data } = await api.get(`/users/${uid}/approver-assignments`);
            setPayload(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { loadUsers(); }, []);
    useEffect(() => { loadAssignment(selectedUserId); }, [selectedUserId]);

    const toggle = (field, id) => {
        const current = payload?.assignment?.[field] || [];
        const next = current.includes(id)
            ? current.filter((x) => x !== id)
            : [...current, id];

        setPayload({
            ...payload,
            assignment: {
                ...payload.assignment,
                [field]: next,
            },
        });
    };

    const save = async () => {
        if (!payload?.user?.id) return;

        try {
            await api.put(`/users/${payload.user.id}/approver-assignments`, {
                branch_manager_ids: payload.assignment.branch_manager_ids || [],
                treasury_ids: payload.assignment.treasury_ids || [],
                accounting_ids: payload.assignment.accounting_ids || [],
                active: payload.assignment.active !== false,
            });
            toast.success("Approver assignments saved");
            await loadAssignment(payload.user.id);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const pool = payload?.approver_pool || [];
    const usersFor = (role) => pool.filter((u) => u.role === role);

    const selectedUser = payload?.user;
    const assignment = payload?.assignment || {};

    return (
        <div className="p-6 space-y-4" data-testid="approver-assignments-page">
            <div>
                <div className="sc-section-title">Administration</div>
                <h1 className="text-2xl font-semibold tracking-tight">Approver Assignments</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Assign exact Branch Manager, Treasury, and Accounting approvers per Sales Agent or Branch Manager.
                </p>
            </div>

            <div className="sc-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                    <label className="text-xs text-muted-foreground">User / Agent</label>
                    <div className="relative mt-1 mb-2">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input className="sc-input pl-9" placeholder="Search agent by name, email…" value={aq} onChange={(e) => setAq(e.target.value)} data-testid="approver-user-search" />
                    </div>
                    <select
                        className="sc-input"
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                        {users
                            .filter((u) => ["sales_agent", "branch_manager"].includes(u.role))
                            .filter((u) => { const s = aq.trim().toLowerCase(); return !s || [u.name, u.email, u.role].some((v) => String(v || "").toLowerCase().includes(s)); })
                            .map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.name || u.email} · {u.email} · {u.role}
                                </option>
                            ))}
                    </select>
                </div>

                <div>
                    <label className="text-xs text-muted-foreground">Branch / Company</label>
                    <div className="mt-2 text-sm">
                        {selectedUser?.branch || "—"}
                    </div>
                </div>
            </div>

            {payload && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <ApproverBox
                        title="Branch Manager Approvers"
                        users={usersFor("branch_manager")}
                        selected={assignment.branch_manager_ids || []}
                        onToggle={(id) => toggle("branch_manager_ids", id)}
                    />

                    <ApproverBox
                        title="Treasury Approvers"
                        users={usersFor("treasury")}
                        selected={assignment.treasury_ids || []}
                        onToggle={(id) => toggle("treasury_ids", id)}
                    />

                    <ApproverBox
                        title="Accounting Approvers"
                        users={usersFor("accounting")}
                        selected={assignment.accounting_ids || []}
                        onToggle={(id) => toggle("accounting_ids", id)}
                    />
                </div>
            )}

            <div className="flex justify-end">
                <button
                    onClick={save}
                    className="h-9 px-3 inline-flex items-center gap-1 rounded bg-foreground text-background text-sm"
                    data-testid="approver-assignments-save"
                >
                    <Save className="w-4 h-4" /> Save Assignments
                </button>
            </div>
        </div>
    );
}

function ApproverBox({ title, users, selected, onToggle }) {
    return (
        <div className="sc-card p-4">
            <div className="font-semibold mb-3">{title}</div>

            {users.length === 0 && (
                <div className="text-sm text-muted-foreground">No active users for this role.</div>
            )}

            <div className="space-y-2">
                {users.map((u) => (
                    <label key={u.id} className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={selected.includes(u.id)}
                            onChange={() => onToggle(u.id)}
                            className="mt-1"
                        />
                        <span>
                            <span className="font-medium">{u.name || u.email}</span>
                            <span className="block text-xs text-muted-foreground font-mono">{u.email}</span>
                            <span className="block text-xs text-muted-foreground">{u.branch || "—"}</span>
                        </span>
                    </label>
                ))}
            </div>
        </div>
    );
}
